import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { logQuestionUpdated } from './auditService';

/**
 * Question Service
 * 
 * Handles all CRUD operations for questions in the Trilogy system.
 * Implements validation, key generation, and audit logging.
 */

// Valid pillar values
const VALID_PILLARS = ['LEAD', 'EVOLVE', 'ALIGN', 'PERFORM'];

// Valid question types
const VALID_TYPES = ['scored', 'bonus'];

/**
 * Validates question data
 * @param {Object} questionData - Question data to validate
 * @throws {Error} If validation fails
 */
function validateQuestionData(questionData) {
  // Validate pillar
  if (!VALID_PILLARS.includes(questionData.pillar)) {
    throw new Error(`Invalid pillar: ${questionData.pillar}. Must be one of: ${VALID_PILLARS.join(', ')}`);
  }

  // Validate type
  if (!VALID_TYPES.includes(questionData.type)) {
    throw new Error(`Invalid type: ${questionData.type}. Must be one of: ${VALID_TYPES.join(', ')}`);
  }

  // Validate multilingual text
  if (!questionData.text || !questionData.text.en) {
    throw new Error('Question text in English is required');
  }

  // Validate weight
  if (questionData.weight !== undefined && (typeof questionData.weight !== 'number' || questionData.weight <= 0)) {
    throw new Error('Weight must be a positive number');
  }

  // Validate maxScore
  if (questionData.maxScore !== undefined && (typeof questionData.maxScore !== 'number' || questionData.maxScore < 0)) {
    throw new Error('Max score must be a non-negative number');
  }

  // Validate orderIndex
  if (questionData.orderIndex !== undefined && (typeof questionData.orderIndex !== 'number' || questionData.orderIndex < 0)) {
    throw new Error('Order index must be a non-negative number');
  }
}

/**
 * Generates a unique question key
 * @param {string} pillar - Question pillar
 * @param {string} type - Question type
 * @param {Array} existingQuestions - Existing questions to check for conflicts
 * @returns {string} Generated key
 */
export function generateQuestionKey(pillar, type, existingQuestions = []) {
  const prefix = type === 'bonus' ? 'bonus' : pillar.toLowerCase();
  const suffix = type === 'bonus' ? 'b' : 'q';

  // Find existing keys with this prefix
  const existingKeys = existingQuestions
    .filter(q => q.key && q.key.startsWith(prefix))
    .map(q => q.key);

  // Extract numbers from existing keys
  const numbers = existingKeys.map(key => {
    const match = key.match(/\d+$/);
    return match ? parseInt(match[0], 10) : 0;
  });

  // Get next number
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  const nextNumber = maxNumber + 1;

  return `${prefix}.${suffix}${nextNumber}`;
}

/**
 * Creates a new question
 * @param {Object} questionData - Question data
 * @param {string} adminId - Admin user ID
 * @returns {Promise<Object>} Created question with ID
 */
export async function createQuestion(questionData, adminId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    // Validate question data
    validateQuestionData(questionData);

    // Get existing questions to generate unique key
    const existingQuestions = await getAllQuestions();

    // Generate key if not provided
    const key = questionData.key || generateQuestionKey(
      questionData.pillar,
      questionData.type,
      existingQuestions
    );

    // Check for duplicate key
    const duplicate = existingQuestions.find(q => q.key === key);
    if (duplicate) {
      throw new Error(`Question key already exists: ${key}`);
    }

    // Set defaults for scored questions
    const defaults = questionData.type === 'scored' ? {
      maxScore: 5,
      weight: 1
    } : {
      maxScore: 0,
      weight: 1
    };

    // Prepare question document
    const questionDoc = {
      key,
      text: questionData.text,
      helpText: questionData.helpText || { en: '', mr: '' },
      pillar: questionData.pillar,
      type: questionData.type,
      weight: questionData.weight !== undefined ? questionData.weight : defaults.weight,
      maxScore: questionData.maxScore !== undefined ? questionData.maxScore : defaults.maxScore,
      orderIndex: questionData.orderIndex !== undefined ? questionData.orderIndex : 999,
      active: questionData.active !== undefined ? questionData.active : true,
      bonusChoices: questionData.bonusChoices || [],
      createdBy: adminId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: 1
    };

    // Add to Firestore
    const docRef = await addDoc(collection(db, 'questions'), questionDoc);

    return {
      id: docRef.id,
      ...questionDoc,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating question:', error);
    throw error;
  }
}

/**
 * Updates an existing question
 * @param {string} questionId - Question document ID
 * @param {Object} updates - Fields to update
 * @param {string} adminId - Admin user ID
 * @returns {Promise<Object>} Updated question
 */
export async function updateQuestion(questionId, updates, adminId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    // Get existing question
    const questionRef = doc(db, 'questions', questionId);
    const questionSnap = await getDoc(questionRef);

    if (!questionSnap.exists()) {
      throw new Error(`Question not found: ${questionId}`);
    }

    const existingQuestion = { id: questionSnap.id, ...questionSnap.data() };

    // Merge updates with existing data for validation
    const updatedData = { ...existingQuestion, ...updates };
    validateQuestionData(updatedData);

    // Prepare update document
    const updatePayload = {
      ...updates,
      updatedAt: serverTimestamp(),
      version: (existingQuestion.version || 1) + 1
    };

    // Update in Firestore
    await updateDoc(questionRef, updatePayload);

    return {
      ...existingQuestion,
      ...updatePayload,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating question:', error);
    throw error;
  }
}

/**
 * Soft deletes a question (sets active to false)
 * @param {string} questionId - Question document ID
 * @param {string} adminId - Admin user ID
 * @returns {Promise<void>}
 */
export async function deactivateQuestion(questionId, adminId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const questionRef = doc(db, 'questions', questionId);
    const questionSnap = await getDoc(questionRef);

    if (!questionSnap.exists()) {
      throw new Error(`Question not found: ${questionId}`);
    }

    await updateDoc(questionRef, {
      active: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deactivating question:', error);
    throw error;
  }
}

/**
 * Gets all questions with optional filtering
 * @param {Object} filters - Filter options
 * @param {boolean} filters.activeOnly - Only return active questions
 * @param {string} filters.pillar - Filter by pillar
 * @param {string} filters.type - Filter by type
 * @returns {Promise<Array>} Array of questions
 */
export async function getAllQuestions(filters = {}) {
  if (!db) {
    console.warn('Firestore is not configured');
    return [];
  }

  try {
    const questionsRef = collection(db, 'questions');
    let q = questionsRef;

    // Build query with filters
    const constraints = [];

    if (filters.activeOnly) {
      constraints.push(where('active', '==', true));
    }

    if (filters.pillar) {
      constraints.push(where('pillar', '==', filters.pillar));
    }

    if (filters.type) {
      constraints.push(where('type', '==', filters.type));
    }

    // Always order by orderIndex
    // constraints.push(orderBy('orderIndex', 'asc'));

    if (constraints.length > 0) {
      q = query(questionsRef, ...constraints);
    }

    const snapshot = await getDocs(q);
    const questions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort in memory to avoid needing a composite index
    return questions.sort((a, b) => {
      const orderA = a.orderIndex !== undefined ? a.orderIndex : 999;
      const orderB = b.orderIndex !== undefined ? b.orderIndex : 999;
      return orderA - orderB;
    });
  } catch (error) {
    console.error('Error getting questions:', error);
    throw error;
  }
}

/**
 * Gets published questions (from latest version snapshot)
 * This will be implemented in versionService, but placeholder here
 * @returns {Promise<Array>} Array of published questions
 */
export async function getPublishedQuestions() {
  // This will fetch from question_versions collection
  // Placeholder for now - will be implemented in versionService
  return getAllQuestions({ activeOnly: true });
}

/**
 * Bulk updates multiple questions
 * @param {Array<string>} questionIds - Array of question IDs
 * @param {Object} updates - Fields to update
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email (optional)
 * @returns {Promise<Object>} Results with success and failure counts
 */
export async function bulkUpdateQuestions(questionIds, updates, adminId, adminEmail = '') {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  const results = {
    success: [],
    failed: []
  };

  const questionsToUpdate = [];

  try {
    const batch = writeBatch(db);

    // First pass: validate all questions
    for (const questionId of questionIds) {
      try {
        const questionRef = doc(db, 'questions', questionId);
        const questionSnap = await getDoc(questionRef);

        if (!questionSnap.exists()) {
          results.failed.push({
            id: questionId,
            reason: 'Question not found'
          });
          continue;
        }

        const existingQuestion = { id: questionSnap.id, ...questionSnap.data() };
        const updatedData = { ...existingQuestion, ...updates };

        // Validate merged data
        validateQuestionData(updatedData);

        const updatePayload = {
          ...updates,
          updatedAt: serverTimestamp(),
          version: (existingQuestion.version || 1) + 1
        };

        batch.update(questionRef, updatePayload);

        questionsToUpdate.push({
          id: questionId,
          before: existingQuestion,
          after: { ...existingQuestion, ...updatePayload }
        });

        results.success.push(questionId);
      } catch (error) {
        results.failed.push({
          id: questionId,
          reason: error.message
        });
      }
    }

    // Commit batch
    await batch.commit();

    // Create audit logs for each successful update
    for (const { id, before, after } of questionsToUpdate) {
      try {
        await logQuestionUpdated(id, adminId, adminEmail, before, after);
      } catch (error) {
        console.error(`Failed to create audit log for question ${id}:`, error);
        // Don't fail the whole operation if audit logging fails
      }
    }

    return results;
  } catch (error) {
    console.error('Error in bulk update:', error);
    throw error;
  }
}

/**
 * Clones a question
 * @param {string} questionId - Question ID to clone
 * @param {string} adminId - Admin user ID
 * @returns {Promise<Object>} Cloned question
 */
export async function cloneQuestion(questionId, adminId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    // Get original question
    const questionRef = doc(db, 'questions', questionId);
    const questionSnap = await getDoc(questionRef);

    if (!questionSnap.exists()) {
      throw new Error(`Question not found: ${questionId}`);
    }

    const originalQuestion = questionSnap.data();

    // Get all questions to generate unique key
    const existingQuestions = await getAllQuestions();

    // Generate new unique key
    const newKey = generateQuestionKey(
      originalQuestion.pillar,
      originalQuestion.type,
      existingQuestions
    );

    // Create cloned question (inactive by default)
    const clonedData = {
      ...originalQuestion,
      key: newKey,
      active: false, // Clones are inactive by default
      createdBy: adminId,
      version: 1
    };

    // Remove fields that shouldn't be copied
    delete clonedData.createdAt;
    delete clonedData.updatedAt;

    // Create new question
    return await createQuestion(clonedData, adminId);
  } catch (error) {
    console.error('Error cloning question:', error);
    throw error;
  }
}

/**
 * Reorders questions
 * @param {Array<Object>} reorderedList - Array of {id, orderIndex} objects
 * @param {string} adminId - Admin user ID
 * @returns {Promise<void>}
 */
export async function reorderQuestions(reorderedList, adminId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const batch = writeBatch(db);

    for (const item of reorderedList) {
      const questionRef = doc(db, 'questions', item.id);
      batch.update(questionRef, {
        orderIndex: item.orderIndex,
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error reordering questions:', error);
    throw error;
  }
}

/**
 * Gets a single question by ID
 * @param {string} questionId - Question document ID
 * @returns {Promise<Object>} Question data
 */
export async function getQuestionById(questionId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const questionRef = doc(db, 'questions', questionId);
    const questionSnap = await getDoc(questionRef);

    if (!questionSnap.exists()) {
      throw new Error(`Question not found: ${questionId}`);
    }

    return {
      id: questionSnap.id,
      ...questionSnap.data()
    };
  } catch (error) {
    console.error('Error getting question:', error);
    throw error;
  }
}
